import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Input component with cross-browser numeric support.
 *
 * When type="number" is passed, we render type="text" with inputMode so that
 * e.target.value is always the raw string the user typed — consistent across
 * Chrome, Firefox, and Edge. The numeric keyboard still appears on mobile
 * thanks to inputMode="decimal" / "numeric".
 */
const Input = React.forwardRef(({ className, type, onKeyDown, onChange, step, min, max, ...props }, ref) => {
  const isNumeric = type === 'number';
  const allowDecimal = isNumeric && (step === 'any' || (step != null && String(step).includes('.')));

  const handleKeyDown = (e) => {
    if (isNumeric) {
      // Block e/E (scientific notation — not valid for our numeric fields)
      if (['e', 'E'].includes(e.key)) {
        e.preventDefault();
        return;
      }
      // Block decimal point if decimals not allowed
      if (!allowDecimal && e.key === '.') {
        e.preventDefault();
        return;
      }
    }
    onKeyDown?.(e);
  };

  const handleChange = (e) => {
    if (isNumeric) {
      const raw = e.target.value;
      if (raw === '') {
        onChange?.(e);
        return;
      }
      const pattern = allowDecimal
        ? /^-?\d*\.?\d*$/
        : /^-?\d*$/;
      if (!pattern.test(raw)) return;
      onChange?.(e);
    } else {
      onChange?.(e);
    }
  };

  // For numeric inputs, render as text with inputMode for cross-browser consistency
  const resolvedType = isNumeric ? 'text' : type;
  const inputMode = isNumeric ? (allowDecimal ? 'decimal' : 'numeric') : undefined;

  return (
    <input
      type={resolvedType}
      inputMode={inputMode}
      step={step}
      min={min}
      max={max}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        type === "date" && "relative block pr-3 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:cursor-pointer",
        className
      )}
      ref={ref}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
